// src/components/PostContent.tsx
import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ReactPlayer from 'react-player/lazy';
import { RedditPost } from '../types/reddit';
import { rewriteXLink, rewriteRedditLinks } from '../utils/linkUtils';
// Import the lightbox library
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

// --- Helper Functions ---

function decodeHtmlEntities(text: string | undefined | null): string {
    if (!text) return '';
    try {
        // Use a temporary DOM element for robust decoding
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value || text; // Fallback to original text if decoding fails
    } catch (e) {
        console.error("Failed to decode HTML entities:", e);
        // Basic fallback replacements (less reliable)
        return text.replace(/</g, '<').replace(/>/g, '>').replace(/"/g, '"').replace(/'/g, "'").replace(/&/g, '&');
    }
}


function getPreviewImageUrl(post: RedditPost, minWidth = 600): string | null {
    const previews = post.preview?.images?.[0]?.resolutions;
    if (!previews || previews.length === 0) {
        const sourceUrl = post.preview?.images?.[0]?.source?.url;
        if (sourceUrl) return decodeHtmlEntities(sourceUrl);
        // Use thumbnail only if it looks like a valid URL
        if (post.thumbnail && post.thumbnail.startsWith('http')) {
            return decodeHtmlEntities(post.thumbnail);
        }
        return null;
    }
    // Find the best resolution >= minWidth, otherwise fallback to largest available
    let bestUrl = decodeHtmlEntities(previews[previews.length - 1]!.url); // Start with largest
    for (let i = previews.length - 1; i >= 0; i--) {
        if (previews[i]!.width >= minWidth) {
            bestUrl = decodeHtmlEntities(previews[i]!.url);
        } else {
            // Once below minWidth, the previous bestUrl is the one we want
            break;
        }
    }
    return bestUrl;
}

// Function to get the highest quality URL for lightbox/zoom
function getBestQualityImageUrl(post: RedditPost, mediaId?: string): string | null {
     // 1. Check gallery metadata if mediaId is provided
     if (mediaId && post.media_metadata?.[mediaId]) {
        const item = post.media_metadata[mediaId];
        if (item && item.status === 'valid') {
            // Prefer source 's' (static or gif), then largest preview 'p'
            const sourceUrl = item.s?.u || item.s?.gif;
            if (sourceUrl) return decodeHtmlEntities(sourceUrl);
            const largestPreviewUrl = item.p?.[item.p.length - 1]?.u;
             if (largestPreviewUrl) return decodeHtmlEntities(largestPreviewUrl);
        }
     }
     // 2. Check post preview source (often higher quality than resolutions)
     const sourceUrl = post.preview?.images?.[0]?.source?.url;
     if (sourceUrl) return decodeHtmlEntities(sourceUrl);
     // 3. Check post URL if it's a direct image link
     const decodedUrl = decodeHtmlEntities(post.url);
     const isDirectImageLink = /\.(jpg|jpeg|png|gif|webp|avif|jxl)$/i.test(decodedUrl);
     if (isDirectImageLink) return rewriteRedditLinks(rewriteXLink(decodedUrl));
     // 4. Fallback to standard preview image function (largest resolution)
     return getPreviewImageUrl(post);
}


function getGfycatRedgifsEmbedUrl(url: string): string | null {
     try {
        const parsedUrl = new URL(url);
        const hostname = parsedUrl.hostname.toLowerCase();
        const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
        // Handle both gfycat.com/slug and redgifs.com/watch/slug formats
        if ((hostname.includes('gfycat.com') || hostname.includes('redgifs.com')) && pathSegments.length > 0) {
            // Get the last segment, which should be the slug
            const slug = pathSegments[pathSegments.length - 1];
            const embedHost = 'redgifs.com'; // Standardize to redgifs
            // Basic slug validation
            if (slug && /^[a-zA-Z0-9-_]+$/.test(slug)) {
                // Construct embed URL (consider adding parameters like autoplay, mute if needed)
                return `https://www.${embedHost}/ifr/${slug}`;
            } else {
                console.warn(`Invalid slug for Gfycat/Redgifs: ${slug} in URL: ${url}`);
            }
        }
    } catch (e) { /* Ignore invalid URLs */ }
    return null;
}

// --- Component Interfaces ---

interface PostContentProps {
    post: RedditPost;
}

interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    fallbackLink?: string;
    onClick?: React.MouseEventHandler<HTMLImageElement>;
    clickable?: boolean;
}

interface LinkMinimalProps {
    post: RedditPost;
}

interface PollDisplayMinimalProps {
    pollData: NonNullable<RedditPost['poll_data']>;
    postId: string;
}


// --- Main Component ---

const PostContent: React.FC<PostContentProps> = ({ post }) => {
    const [galleryIndex, setGalleryIndex] = useState(0);
    // Lightbox state
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSlides, setLightboxSlides] = useState<{ src: string }[]>([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);


    // Reset gallery index when the post ID changes
    useEffect(() => { setGalleryIndex(0); }, [post.id]);

    const isGallery = post.is_gallery === true;
    const galleryItems = post.gallery_data?.items;
    const mediaMetadata = post.media_metadata;

    const handleGalleryNext = () => { if (galleryItems) { setGalleryIndex((prev) => (prev + 1) % galleryItems.length); } };
    const handleGalleryPrev = () => { if (galleryItems) { setGalleryIndex((prev) => (prev - 1 + galleryItems.length) % galleryItems.length); } };

    // Function to open the lightbox
    const openLightbox = (index: number, slides: { src: string }[]) => {
        if (slides.length > 0 && slides[index]?.src) { // Ensure the target slide has a src
            setLightboxIndex(index);
            setLightboxSlides(slides);
            setLightboxOpen(true);
        } else {
            console.warn("Attempted to open lightbox with invalid slides or index:", index, slides);
        }
    };

    // Prepare slides for gallery lightbox
    const gallerySlides = useMemo(() => {
        if (!isGallery || !galleryItems || !mediaMetadata) return [];
        return galleryItems.map(item => ({
            src: getBestQualityImageUrl(post, item.media_id) || '' // Use best quality for lightbox
        })).filter(slide => slide.src); // Filter out items where URL couldn't be resolved
    }, [isGallery, galleryItems, mediaMetadata, post]);


    const getImageUrlFromMetadata = (mediaId: string): string | null => {
         const item = mediaMetadata?.[mediaId];
        if (!item || item.status !== 'valid') return null;
        // Prefer source 's', then largest preview 'p' (for display, not lightbox)
        const sourceUrl = item.s?.u || item.s?.gif;
        const largestPreviewUrl = item.p?.[item.p.length - 1]?.u;
        return decodeHtmlEntities(sourceUrl || largestPreviewUrl || null);
    };

    // --- Determine Media Type and Render ---
    // This function now returns an array of React Nodes
    const renderMediaElements = (): React.ReactNode[] => {
        const mediaNodes: React.ReactNode[] = [];
        let hasPrimaryVisualMedia = false; // Flag to avoid redundant simple image if video/gallery exists

        const decodedUrl = decodeHtmlEntities(post.url);

        // 1. Gallery (Primary Visual)
        if (isGallery && galleryItems && mediaMetadata) {
            hasPrimaryVisualMedia = true;
            const currentItem = galleryItems[galleryIndex];
            const currentMediaId = currentItem?.media_id;
            // Use a reasonable preview resolution for inline display
            const currentDisplayImageUrl = currentMediaId ? getImageUrlFromMetadata(currentMediaId) : null;
            const outboundUrl = currentItem?.outbound_url;
            const decodedOutboundUrl = decodeHtmlEntities(outboundUrl);
            const processedOutboundUrl = rewriteRedditLinks(rewriteXLink(decodedOutboundUrl));

            mediaNodes.push(
                 <div key="gallery" className="relative group my-6 -mx-4 sm:mx-0">
                    <div className="relative aspect-video flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
                        {currentDisplayImageUrl ? (
                            <ImageWithFallback
                                key={`${currentMediaId}-${galleryIndex}`}
                                src={currentDisplayImageUrl}
                                alt={currentItem?.caption || `Gallery image ${galleryIndex + 1}`}
                                fallbackLink={`https://www.reddit.com${post.permalink}`}
                                className="max-w-full max-h-[75vh] object-contain animate-fade-in"
                                // Open lightbox with all gallery images
                                onClick={() => openLightbox(galleryIndex, gallerySlides)}
                                clickable={true}
                            />
                        ) : (
                            <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 h-full p-4 text-center text-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                <span>Image not available</span>
                                <a href={`https://www.reddit.com${post.permalink}`} target="_blank" rel="noopener noreferrer" className='text-indigo-600 dark:text-indigo-400 hover:underline text-xs mt-1'>View on Reddit</a>
                            </div>
                        )}
                    </div>
                    {galleryItems.length > 1 && (
                        <>
                            <button onClick={handleGalleryPrev} aria-label="Previous image" className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/30 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-150 ease-in-out z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            <button onClick={handleGalleryNext} aria-label="Next image" className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/30 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-150 ease-in-out z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                            </button>
                        </>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent p-3 pt-8 text-white text-xs pointer-events-none">
                        {galleryItems.length > 1 && (
                            <div className="absolute top-2 right-2 bg-black/40 px-1.5 py-0.5 rounded-full text-[10px] z-10 pointer-events-auto">
                                {galleryIndex + 1} / {galleryItems.length}
                            </div>
                        )}
                        {currentItem?.caption && <p className="mb-1 line-clamp-2">{decodeHtmlEntities(currentItem.caption)}</p>}
                        {processedOutboundUrl && (
                            <a href={processedOutboundUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-300 hover:underline break-all flex items-center pointer-events-auto" onClick={(e) => e.stopPropagation()}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" /><path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" /></svg>
                                <span className="truncate">{processedOutboundUrl?.replace(/^https?:\/\//, '')}</span>
                            </a>
                        )}
                    </div>
                </div>
            );
        }

        // 2. Videos/Gifs (Primary Visual)
        const redditVideo = post.media?.reddit_video;
        const canPlayUrl = ReactPlayer.canPlay(decodedUrl);
        const isVideoHint = post.post_hint === 'hosted:video' || post.post_hint === 'rich:video';
        const isCommonProvider = /youtube\.com|youtu\.be|vimeo\.com|streamable\.com|dailymotion\.com|twitch\.tv/i.test(post.domain);
        const gfycatEmbedUrl = getGfycatRedgifsEmbedUrl(decodedUrl);
        let videoUrlToPlay: string | null = null;
        let isIframeEmbed = false;

        if (post.is_video && redditVideo) {
            videoUrlToPlay = decodeHtmlEntities(redditVideo.hls_url || redditVideo.dash_url || redditVideo.fallback_url);
        } else if (gfycatEmbedUrl) {
            videoUrlToPlay = gfycatEmbedUrl;
            isIframeEmbed = true;
        } else if (canPlayUrl && (isVideoHint || isCommonProvider || /\.(mp4|webm|ogg|mov|m4v)$/i.test(decodedUrl))) {
            // Ensure we don't play direct image links as videos if ReactPlayer mistakenly says it can play them
            if (!/\.(jpg|jpeg|png|gif|webp|avif|jxl)$/i.test(decodedUrl)) {
                 videoUrlToPlay = rewriteRedditLinks(rewriteXLink(decodedUrl));
            }
        }

        if (videoUrlToPlay) {
            hasPrimaryVisualMedia = true;
            const posterUrl = getPreviewImageUrl(post) || (post.thumbnail && post.thumbnail.startsWith('http') ? decodeHtmlEntities(post.thumbnail) : null);
            mediaNodes.push(
                <div key="video" className="relative aspect-video bg-gray-900 dark:bg-black rounded-lg overflow-hidden my-6 -mx-4 sm:mx-0 shadow-sm">
                    {isIframeEmbed ? (
                        <iframe src={videoUrlToPlay} frameBorder="0" scrolling="no" width="100%" height="100%" className="absolute top-0 left-0 w-full h-full" allowFullScreen title={`${post.domain} embed`} loading="lazy"></iframe>
                    ) : (
                        <ReactPlayer url={videoUrlToPlay} controls width="100%" height="100%" className="absolute top-0 left-0" light={posterUrl || false} playing={false} config={{ file: { attributes: { poster: posterUrl || undefined, preload: 'metadata' } } }}/>
                    )}
                </div>
            );
        }

        // 3. Embedded Media (oEmbed iframe) (Primary Visual)
        const oembedHtml = post.media?.oembed?.html;
        if (!hasPrimaryVisualMedia && oembedHtml && post.post_hint === 'rich:video' && !canPlayUrl && !gfycatEmbedUrl) {
             const decodedEmbedHtml = decodeHtmlEntities(oembedHtml);
             if (decodedEmbedHtml.trim().startsWith('<iframe')) {
                 hasPrimaryVisualMedia = true;
                 const aspectRatio = (post.media?.oembed?.height && post.media?.oembed?.width && post.media.oembed.width > 0)
                     ? `${(post.media.oembed.height / post.media.oembed.width) * 100}%` : '56.25%'; // Default 16:9
                 mediaNodes.push(
                    <div key="oembed" style={{ paddingBottom: aspectRatio }} className="relative h-0 [&>iframe]:absolute [&>iframe]:top-0 [&>iframe]:left-0 [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:rounded-lg my-6 -mx-4 sm:mx-0 overflow-hidden shadow-sm" dangerouslySetInnerHTML={{ __html: decodedEmbedHtml }}/>
                 );
             }
        }

        // 4. Image Post (Check only if no primary visual media OR if it's explicitly an image hint/URL)
        const hasImageHint = post.post_hint === 'image';
        const isDirectImageLink = /\.(jpg|jpeg|png|gif|webp|avif|jxl)$/i.test(decodedUrl);
        const potentialImageUrl = (hasImageHint || isDirectImageLink) ? decodedUrl : null;
        const processedPotentialImageUrl = rewriteRedditLinks(rewriteXLink(potentialImageUrl));
        // Use preview for inline display
        const displayImageUrl = getPreviewImageUrl(post) || processedPotentialImageUrl;
        // Use best quality for lightbox
        const bestQualityImageUrl = getBestQualityImageUrl(post);

        // Add image if found and no primary visual media has been added yet, OR if it's explicitly an image post
        if (displayImageUrl && (!hasPrimaryVisualMedia || hasImageHint || isDirectImageLink)) {
             // Avoid adding if it's the same source as the video poster? (Complex check, maybe omit for simplicity)
             mediaNodes.push(
                 <div key="image" className="my-6 flex justify-center">
                    <ImageWithFallback
                        src={displayImageUrl}
                        alt={post.title}
                        fallbackLink={rewriteRedditLinks(rewriteXLink(decodedUrl))}
                        className="max-w-full h-auto max-h-[80vh] object-contain rounded mx-auto"
                        // Open lightbox with this single image
                        onClick={() => bestQualityImageUrl ? openLightbox(0, [{ src: bestQualityImageUrl }]) : null}
                        clickable={!!bestQualityImageUrl} // Only clickable if we have a URL for the lightbox
                    />
                 </div>
             );
        }

        // 5. Link Component (Add if hint is 'link' OR if it's not self/video/gallery/embed and URL exists)
        const shouldShowLink = post.post_hint === 'link' ||
                              (!post.is_self && !hasPrimaryVisualMedia && !isGallery && !isVideoHint && !gfycatEmbedUrl && decodedUrl && !isDirectImageLink);

        if (shouldShowLink) {
             mediaNodes.push(<LinkMinimal key="link" post={post} />);
        }

        // 6. Poll
        if (post.poll_data) {
            mediaNodes.push(<PollDisplayMinimal key="poll" pollData={post.poll_data} postId={post.id} />);
        }

        return mediaNodes;
    };

    const mediaElements = renderMediaElements();
    const hasSelfText = post.selftext && post.selftext.trim() !== '';

    // Markdown components for selftext
    const selftextMarkdownComponents: Components = {
        a: ({ node, href, children, ...props }) => {
            const processedHref = rewriteRedditLinks(rewriteXLink(href));
            const isInternalLink = processedHref?.startsWith(window.location.origin) || processedHref?.startsWith('/');
            return <a href={processedHref || '#'} target={isInternalLink ? '_self' : '_blank'} rel="noopener noreferrer" {...props}>{children}</a>;
        },
        // Make images in selftext clickable to open in lightbox
        img: ({ node, src, alt, ...props }) => {
            const bestQualitySrc = src ? rewriteRedditLinks(rewriteXLink(src)) : null; // Assume src is best quality here
            return (
                <ImageWithFallback
                    src={src}
                    alt={alt}
                    className="my-2 rounded max-w-full h-auto border dark:border-gray-700"
                    loading="lazy"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    onClick={() => bestQualitySrc ? openLightbox(0, [{ src: bestQualitySrc }]) : null}
                    clickable={!!bestQualitySrc}
                    {...props}
                 />
            );
        },
    };

    return (
        <>
            {/* Render all collected media elements */}
            {mediaElements.length > 0 && (
                <div className="space-y-6">
                    {mediaElements.map((el, index) => (
                        <React.Fragment key={index}>{el}</React.Fragment>
                    ))}
                </div>
            )}

            {/* Render selftext if it exists */}
            {hasSelfText && (
                // Apply prose styles directly to the selftext container
                // Add margin top if there were media elements above it
                <div className={`prose prose-base dark:prose-invert max-w-none leading-relaxed ${mediaElements.length > 0 ? 'mt-8' : ''}`}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={selftextMarkdownComponents}>
                        {post.selftext}
                    </ReactMarkdown>
                </div>
            )}

            {/* Message for self-posts with no text and no other media */}
            {post.is_self && !hasSelfText && mediaElements.length === 0 && (
                <p className="text-gray-500 dark:text-gray-400 italic text-sm text-center py-4">(No text content)</p>
            )}

            {/* Lightbox Component (remains the same) */}
            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={lightboxSlides}
                index={lightboxIndex}
                plugins={[Zoom]} // Enable zoom plugin
                // Optional: Configure zoom plugin
                zoom={{
                    maxZoomPixelRatio: 5, // Default 1, increase max zoom
                    zoomInMultiplier: 1.5, // Default 2
                    doubleTapDelay: 300, // Default 300
                    doubleClickDelay: 300, // Default 500
                    doubleClickMaxStops: 2, // Default 2
                    keyboardMoveDistance: 50, // Default 50
                    wheelZoomDistanceFactor: 100, // Default 100
                    pinchZoomDistanceFactor: 100, // Default 100
                    scrollToZoom: true, // Default false, enable scroll wheel zoom
                  }}
                // Optional: Add other plugins like Thumbnails, Fullscreen, etc. later
                // plugins={[Zoom, Thumbnails, Fullscreen]}
                // Customize buttons if needed (e.g., hide prev/next for single images)
                 render={{
                     buttonPrev: lightboxSlides.length <= 1 ? () => null : undefined,
                     buttonNext: lightboxSlides.length <= 1 ? () => null : undefined,
                 }}
            />
        </>
    );
};


// --- Internal Sub-Components ---

const LinkMinimal: React.FC<LinkMinimalProps> = ({ post }) => {
    const originalUrl = decodeHtmlEntities(post.url);
    const processedUrl = rewriteRedditLinks(rewriteXLink(originalUrl));
    // Avoid rendering if URL is essentially the same as the post permalink (common for self-posts)
    if (!processedUrl || processedUrl.endsWith(post.permalink)) {
        return null;
    }
    return (
         // Use a distinct key or ensure it's wrapped uniquely if needed
         <div className="my-6">
            <a href={processedUrl} target="_blank" rel="noopener noreferrer nofollow" className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-indigo-400 dark:hover:border-indigo-600 focus:border-indigo-500 transition-colors duration-150 ease-in-out group bg-white dark:bg-gray-800/30 hover:bg-gray-50 dark:hover:bg-gray-800/60">
                <div className="flex items-center text-sm gap-3">
                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" /><path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" /></svg>
                    {/* Display domain if available, otherwise fallback to URL */}
                    <span className="text-gray-600 dark:text-gray-300 truncate shrink overflow-hidden flex-1 font-medium" title={processedUrl}>{post.domain || processedUrl.replace(/^https?:\/\//, '').split('/')[0]}</span>
                    <span className="text-indigo-600 dark:text-indigo-400 text-xs font-semibold group-hover:underline flex items-center flex-nowrap whitespace-nowrap ml-auto pl-2">Open Link</span>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5 truncate" title={processedUrl}>{processedUrl}</p>
            </a>
         </div>
    );
};

const PollDisplayMinimal: React.FC<PollDisplayMinimalProps> = ({ pollData, postId }) => {
    const { options, total_vote_count, voting_end_timestamp, user_selection } = pollData;
    const isPollEnded = Date.now() > voting_end_timestamp;
    const canShowResults = isPollEnded || user_selection !== null;
    const timeRemaining = (): string => {
        if (isPollEnded) return "Poll ended";
        const remainingMs = voting_end_timestamp - Date.now();
        if (remainingMs <= 0) return "Poll ended";
        const days = Math.floor(remainingMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((remainingMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
        if (days > 0) return `${days}d left`;
        if (hours > 0) return `${hours}h left`;
        if (minutes > 0) return `${minutes}m left`;
        return "Ending soon";
    };
    return (
        <div className="my-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3 bg-white dark:bg-gray-800/30">
            <div className="space-y-2">
                {options.map((option) => {
                    const isSelected = user_selection === option.id;
                    const voteCount = option.vote_count ?? 0;
                    const percentage = (total_vote_count > 0 && canShowResults) ? ((voteCount / total_vote_count) * 100) : 0;
                    return (
                        <div key={`${postId}-${option.id}`} className={`relative p-2.5 border rounded transition-colors duration-150 ease-in-out overflow-hidden text-sm ${isSelected ? 'border-indigo-400 dark:border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50'}`}>
                            {canShowResults && (<div className={`absolute top-0 left-0 bottom-0 bg-indigo-100 dark:bg-indigo-800/40 transition-all duration-300 ease-in-out ${isSelected ? 'opacity-80' : 'opacity-60'}`} style={{ width: `${percentage}%` }} aria-hidden="true"></div>)}
                            <div className="relative z-10 flex justify-between items-center gap-2">
                                <span className={`flex-grow pr-2 ${isSelected ? 'font-semibold text-indigo-900 dark:text-indigo-200' : 'text-gray-800 dark:text-gray-200'}`}>{option.text}</span>
                                {canShowResults && (<span className={`text-xs font-medium whitespace-nowrap ${isSelected ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-400'}`}>{voteCount.toLocaleString()} ({percentage.toFixed(0)}%)</span>)}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700 mt-3">
                <span>{total_vote_count.toLocaleString()} votes</span>
                <span>{timeRemaining()}</span>
            </div>
            {!canShowResults && (<p className="text-xs text-gray-500 dark:text-gray-400 italic text-center">(Results hidden until poll ends or you vote)</p>)}
        </div>
    );
};

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({ src, alt, fallbackLink, className, clickable, onClick, ...props }) => {
    const [error, setError] = useState(false);
    const handleError = () => setError(true);
    useEffect(() => setError(false), [src]);
    const combinedClassName = `${className} ${clickable ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`; // Add hover effect for clickable images
    if (error || !src) {
        return (
            <div className={`flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded p-4 text-xs text-center ${className}`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mb-1 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span>Image failed to load</span>
                {fallbackLink && (<a href={fallbackLink} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline mt-1" onClick={(e) => e.stopPropagation()}>View Source</a>)}
            </div>
        );
    }
    // Pass the onClick handler down to the img element
    return <img src={src} alt={alt || ''} className={combinedClassName} onError={handleError} loading="lazy" onClick={onClick} {...props} />;
};

export default PostContent;